'use client';

import { PropsWithChildren, useState } from 'react';
import { addDays, format } from 'date-fns';
import { Bot, CalendarPlus, Info, PhoneCall, PieChartIcon } from 'lucide-react';
import { DateRange as ReactDateRange } from 'react-day-picker';

import { PageMain } from '@/app/(app)/page-main';
import DateRange from '@/components/daterange';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';
import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ChartConfig } from '@/components/ui/chart';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { OFFICE_IDS } from '@/consts/offices';
import { cn } from '@/lib/utils';

import AnimateValue from './components/animate-value';
import PieChart from './components/pie-chart';

// 🎑 View
export default function VoiceAgentDashboardPageHome() {
  const [fromDate, setFromDate] = useState(() => {
    const date = addDays(new Date(), -7);
    return { date, formatted: format(date, 'yyyy-MM-dd') };
  });
  const [toDate, setToDate] = useState(() => {
    const date = new Date();
    return { date, formatted: format(date, 'yyyy-MM-dd') };
  });
  const { api } = useApiV1Ctx();
  const { officeId } = useOfficeCtx();
  const { data, isLoading } = api.calls.getCallAnalytics.useQuery(
    {
      query: {
        office_id: officeId,
        from_date: fromDate.formatted,
        to_date: toDate.formatted,
      },
    },
    {
      placeholderData: (prev) => prev,
    }
  );

  const handleDateChange = (range: ReactDateRange | undefined) => {
    if (!range) return;
    if (!range.from || !range.to) return;

    const fromFormatted = format(range.from, 'yyyy-MM-dd');
    const toFormatted = format(range.to, 'yyyy-MM-dd');

    if (fromFormatted !== fromDate.formatted) {
      setFromDate({
        date: range.from,
        formatted: fromFormatted,
      });
    }
    if (toFormatted !== toDate.formatted) {
      setToDate({
        date: range.to,
        formatted: toFormatted,
      });
    }
  };

  if (!data) {
    if (isLoading) {
      return (
        <PageMain title='Analytics' right={<DateRange value={{ from: fromDate.date, to: toDate.date }} disabled />}>
          <Skeleton className='min-h-[80dvh] w-full' />
        </PageMain>
      );
    }
    return (
      <div className='flex h-full flex-col items-center justify-center'>
        <div className='flex flex-col gap-2 rounded-2xl bg-background p-8 shadow-md'>
          <PieChartIcon className='size-9' />
          <h2 className='mt-2 text-heading-lg font-semibold'>Analytics</h2>
          <div className='text-sm text-muted-foreground'>
            You don&apos;t have any analytics yet, please check back later.
          </div>
        </div>
      </div>
    );
  }

  const { calls, engagement } = formatData(data);

  return (
    <PageMain
      title='Analytics'
      right={<DateRange value={{ from: fromDate.date, to: toDate.date }} onCommit={handleDateChange} />}
    >
      <div className={cn('space-y-6', isLoading && 'animate-pulse')}>
        <Section icon={PhoneCall} title='Calls' columns={2}>
          <div className='flex flex-col space-y-4'>
            {calls.map((call) => {
              return <CustomCard key={call.label} {...call} />;
            })}
          </div>

          <OfficeVsAfterHoursChart
            label='When did customers call?'
            officeHours={data.calls_during_office_hours}
            afterHours={data.calls_outside_office_hours}
          />
        </Section>

        <AppointmentsSection data={data} />

        <Section icon={Bot} title='Engagement' columns={2}>
          {engagement.map((engagement) => {
            return <CustomCard key={engagement.label} {...engagement} />;
          })}

          {/* <CustomCard label='Call reason' title='TBD' /> */}
        </Section>
      </div>
    </PageMain>
  );
}

// 🍰 Components
type SectionProps = PropsWithChildren & {
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  title: string;
  columns?: 2 | 3 | 4;
  right?: React.ReactNode;
};

function Section({ icon: Icon, title, children, columns = 3, right }: SectionProps) {
  return (
    <div className='flex flex-col gap-2 rounded-xl border bg-background p-3 shadow-xs'>
      <div className='flex items-center justify-between p-2'>
        <div className='flex items-center space-x-2.5'>
          {Icon && <Icon className='size-5 text-muted-foreground' />}
          <h2 className='text-heading-lg font-semibold'>{title}</h2>
        </div>
        {right}
      </div>

      <div
        className={cn('grid gap-4', {
          'lg:grid-cols-2': columns === 2,
          'lg:grid-cols-3': columns === 3,
          'grid-cols-4': columns === 4,
        })}
      >
        {children}
      </div>
    </div>
  );
}

type CustomerFilter = 'all' | 'new' | 'existing';

function AppointmentsSection({ data }: { data: ApiSchemas['CallAnalytics'] }) {
  const [customerFilter, setCustomerFilter] = useState<CustomerFilter>('all');

  const newCustomerBookingRate =
    data.new_customer_intent_to_book > 0
      ? Math.min(Math.round((data.new_customer_booked / data.new_customer_intent_to_book) * 100), 100)
      : 0;
  const existingCustomerBookingRate =
    data.existing_customer_intent_to_book > 0
      ? Math.min(Math.round((data.existing_customer_booked / data.existing_customer_intent_to_book) * 100), 100)
      : 0;

  const bookingRateCard = (() => {
    switch (customerFilter) {
      case 'new':
        return {
          label: 'Booking rate',
          tooltip: `${data.new_customer_booked} booked out of ${data.new_customer_intent_to_book} new customers who intended to book`,
          title: newCustomerBookingRate,
        };
      case 'existing':
        return {
          label: 'Booking rate',
          tooltip: `${data.existing_customer_booked} booked out of ${data.existing_customer_intent_to_book} existing customers who intended to book`,
          title: existingCustomerBookingRate,
        };
      default: {
        const totalBooked = data.new_customer_booked + data.existing_customer_booked;
        const overallRate =
          data.intent_to_book > 0 ? Math.min(Math.round((totalBooked / data.intent_to_book) * 100), 100) : 0;
        return {
          label: 'Booking rate',
          tooltip: `${totalBooked} booked out of ${data.intent_to_book} callers who intended to book`,
          title: overallRate,
        };
      }
    }
  })();

  const conversionRateCard = (() => {
    switch (customerFilter) {
      case 'new': {
        const rate =
          data.new_customer_intent_to_book > 0
            ? Math.min(Math.round((data.new_customer_booked / data.new_customer_intent_to_book) * 100), 100)
            : 0;
        return {
          tooltip: `${data.new_customer_booked} booked out of ${data.new_customer_intent_to_book} new customers who intended to book`,
          title: rate,
        };
      }
      case 'existing': {
        const rate =
          data.existing_customer_intent_to_book > 0
            ? Math.min(Math.round((data.existing_customer_booked / data.existing_customer_intent_to_book) * 100), 100)
            : 0;
        return {
          tooltip: `${data.existing_customer_booked} booked out of ${data.existing_customer_intent_to_book} existing customers who intended to book`,
          title: rate,
        };
      }
      default:
        return {
          tooltip: 'Percentage of callers who called to book an appointment and successfully did',
          title: Math.min(Math.round((data.booked_success / data.intent_to_book) * 100) || 0, 100),
        };
    }
  })();

  const appointmentsBooked = (() => {
    switch (customerFilter) {
      case 'new':
        return data.new_customer_booked;
      case 'existing':
        return data.existing_customer_booked;
      default:
        return data.appointments_booked;
    }
  })();

  const appointmentsRescheduled = (() => {
    switch (customerFilter) {
      case 'new':
        return data.new_customer_rescheduled;
      case 'existing':
        return data.existing_customer_rescheduled;
      default:
        return data.appointments_rescheduled;
    }
  })();

  const appointmentsCancelled = (() => {
    switch (customerFilter) {
      case 'new':
        return data.new_customer_cancelled;
      case 'existing':
        return data.existing_customer_cancelled;
      default:
        return data.appointments_cancelled;
    }
  })();

  return (
    <Section
      icon={CalendarPlus}
      title='Appointments'
      right={
        <Select value={customerFilter} onValueChange={(v) => setCustomerFilter(v as CustomerFilter)}>
          <SelectTrigger className='w-[160px]'>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='all'>All customers</SelectItem>
            <SelectItem value='new'>New customers</SelectItem>
            <SelectItem value='existing'>Existing customers</SelectItem>
          </SelectContent>
        </Select>
      }
    >
      <CustomCard
        label={bookingRateCard.label}
        tooltip={bookingRateCard.tooltip}
        title={bookingRateCard.title}
        format='percentage'
        direction='horizontal'
      />
      <CustomCard
        label='Appointments booked'
        tooltip='Number of appointments successfully booked'
        title={appointmentsBooked}
        format='number'
        direction='horizontal'
      />
      <CustomCard
        label='Revenue generated'
        tooltip='Calculated by multiplying each booked appointment by the price of the service booked. This is an estimate and accuracy depends on the data available in your system'
        title='N/A'
        format='text'
        direction='horizontal'
      />
      <CustomCard
        label='Conversion rate'
        tooltip={conversionRateCard.tooltip}
        title={conversionRateCard.title}
        format='percentage'
        direction='horizontal'
      />
      <CustomCard
        label='Appointments rescheduled'
        tooltip='Number of appointments successfully rescheduled'
        title={appointmentsRescheduled}
        format='number'
        direction='horizontal'
      />
      <CustomCard
        label='Appointments cancelled'
        tooltip='Number of appointments successfully cancelled'
        title={appointmentsCancelled}
        format='number'
        direction='horizontal'
      />
      <CustomCard label='Calls transferred' title={data.transfer_completed} format='number' direction='horizontal' />
    </Section>
  );
}

type CustomCardProps = PropsWithChildren & {
  label: string;
  tooltip?: string;
  title?: string | number;
  format?: 'number' | 'currency' | 'text' | 'percentage';
  direction?: 'horizontal' | 'vertical';
};

function CustomCard({ label, tooltip, title, children, format = 'text', direction = 'vertical' }: CustomCardProps) {
  const formattedTitle = (() => {
    switch (format) {
      case 'number':
        return <AnimateValue style='decimal' value={Number(title)} />;
      case 'currency':
        return <AnimateValue style='currency' value={Number(title)} />;
      case 'percentage':
        return <AnimateValue style='percent' value={Number(title)} />;
      default:
        return title;
    }
  })();

  return (
    <Card className='grow shadow-none'>
      <CardHeader
        className={cn('flex flex-col items-start', {
          'h-12 flex-row items-center justify-between': direction === 'horizontal',
        })}
      >
        <CardDescription className='flex items-center space-x-1'>
          <div>{label}</div>

          {tooltip && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger>
                  <Info className='size-3.5' />
                </TooltipTrigger>

                <TooltipContent className='max-w-64 text-center text-balance'>{tooltip}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
        </CardDescription>

        <CardTitle className='text-3xl font-bold'>{formattedTitle}</CardTitle>
      </CardHeader>

      {children && <div>{children}</div>}
    </Card>
  );
}

type OfficeVsAfterHoursChartProps = CustomCardProps & {
  officeHours: number;
  afterHours: number;
};

function OfficeVsAfterHoursChart({ officeHours, afterHours, ...customCardProps }: OfficeVsAfterHoursChartProps) {
  const data = [
    { type: 'office', hours: officeHours, fill: 'var(--color-office)' },
    { type: 'after', hours: afterHours, fill: 'var(--color-after)' },
  ];

  const config = {
    office: {
      label: 'Office hours',
      color: 'var(--chart-1)',
    },
    after: {
      label: 'After hours',
      color: 'var(--chart-2)',
    },
  } satisfies ChartConfig;

  return (
    <CustomCard {...customCardProps}>
      <div className='flex items-center px-6'>
        <div className='hidden flex-1 space-y-1 lg:block'>
          {data.map((item) => {
            const { type, hours } = item;
            const { label, color } = config[type as keyof typeof config];

            return (
              <div key={type} className='flex items-center space-x-2'>
                <div className='size-4 rounded-sm' style={{ backgroundColor: color }} />
                <p className='text-sm text-muted-foreground'>
                  {label}: {hours}
                </p>
              </div>
            );
          })}
        </div>

        <div className='flex-1'>
          <PieChart config={config} data={data} dataKey='hours' nameKey='type' />
        </div>
      </div>
    </CustomCard>
  );
}

// 🛠️ Utils
function formatData(data: ApiSchemas['CallAnalytics']) {
  // 📞 Calls
  const calls: CustomCardProps[] = [
    {
      label: 'Calls',
      title: data.total_calls,
      format: 'number',
    },
    {
      label: 'Minutes',
      title: data.total_minutes,
      format: 'number',
    },
  ];

  // 🤖 Engagement
  const engagement: CustomCardProps[] = [
    {
      label: 'Spam Calls',
      tooltip: 'Calls with no interaction with the AI',
      title: data.no_engagement,
      format: 'number',
      direction: 'horizontal',
    },
  ];

  const OFFICE_IDS_TO_HIDE_OUTCOME = [OFFICE_IDS.HORIZON, OFFICE_IDS.APOLLOX];

  if (!OFFICE_IDS_TO_HIDE_OUTCOME.includes(data.office_id)) {
    engagement.unshift({
      label: 'Calls where a human was requested',
      tooltip: 'Number of calls where the caller requested to be transferred to a human representative',
      title: data.human_requested,
      format: 'number',
      direction: 'horizontal',
    });
  }

  return { calls, engagement };
}
